import { Farm } from 'state/types'
import BigNumber from 'bignumber.js'
import { BLOCKS_PER_YEAR, CAKE_PER_YEAR } from 'config'
import lpAprs from 'config/constants/lpAprs.json'
import { ChainId } from '../sdk'
import { BIG_ZERO, BIG_TEN } from './bigNumber'
import { getTokenPriceFromAddress } from "./getTokenPriceFromAddress"
import { rewardsAreActive } from "./rewardsAreActive"

/**
 * Get the APR value in %
 * @param stakingTokenPrice Token price in the same quote currency
 * @param rewardTokenPrice Token price in the same quote currency
 * @param totalStaked Total amount of stakingToken in the pool
 * @param tokenPerBlock Amount of new token allocated to the pool for each new block
 * @returns Null if the APR is NaN or infinite.
 */
export const getPoolApr = (
  stakingTokenPrice: number,
  rewardTokenPrice: number,
  totalStaked: number,
  tokenPerBlock: number,
): number => {
  const totalRewardPricePerYear = new BigNumber(rewardTokenPrice).times(tokenPerBlock).times(BLOCKS_PER_YEAR)
  const totalStakingTokenInPool = new BigNumber(stakingTokenPrice).times(totalStaked)
  const apr = totalRewardPricePerYear.div(totalStakingTokenInPool).times(100)
  return apr.isNaN() || !apr.isFinite() ? null : apr.toNumber()
}

/**
 * Get farm APR value in %
 * @param poolWeight allocationPoint / totalAllocationPoint
 * @param tokenPriceUsd Reward token price in USD
 * @param poolLiquidityUsd Total pool liquidity in USD
 * @returns
 */

export const getFarmApr = (
  poolWeight: BigNumber,
  tokenPriceUsd: BigNumber,
  poolLiquidityUsd: BigNumber,
  rewardsPerSecond: BigNumber,
  farmAddress?: string,
): { tokenRewardsApr: number; lpRewardsApr: number } => {

  // Handle case where allocationPoint or totalAllocationPoint is 0
  if (poolWeight.isZero() || poolLiquidityUsd.isZero()) {
    return { tokenRewardsApr: 0, lpRewardsApr: 0 }
  }

  const secondsInYear = new BigNumber(60 * 60 * 24 * 365)
  const yearlyRewardAllocation = rewardsPerSecond.times(secondsInYear).times(poolWeight)

  const tokenRewardsApr = yearlyRewardAllocation
    .times(tokenPriceUsd)
    .div(poolLiquidityUsd)
    .times(100)
    .dividedBy(BIG_TEN.pow(18))

  let tokenRewardsAprAsNumber = 0
  if (!tokenRewardsApr.isNaN() && tokenRewardsApr.isFinite()) {
    tokenRewardsAprAsNumber = tokenRewardsApr.toNumber()
  }

  const lpRewardsApr = farmAddress
    ? lpAprs[farmAddress?.toLocaleLowerCase()] ?? 0
    : 0

  return { tokenRewardsApr: tokenRewardsAprAsNumber, lpRewardsApr }
}


export function getTotalFarmApr(
  farms: Farm[],
  pid: number,
  totalLiquidity: BigNumber,
  masterRewardTokenPrice: BigNumber,
  currentBlock: number
) {
  // Check if farm exists for the given pid
  const farm = farms[pid]
  if (!farm) {
    console.error(`Farm not found for pid: ${pid}`)
    return { aprArray: [], totalApr: 0, lpRewardsApr: 0 }
  }

  // Check if farm.poolWeight and farm.rewardPerSecond are valid
  const poolWeight = new BigNumber(farm.poolWeight)
  const rewardPerSecond = new BigNumber(farm.rewardPerSecond)

  // First get APR for main farm
  const { tokenRewardsApr, lpRewardsApr } = getFarmApr(
    poolWeight,
    masterRewardTokenPrice,
    totalLiquidity,
    rewardPerSecond,
    farm.lpAddresses[ChainId.MAINNET]
  )

  let totalApr = 0.0001 // ensures that display shows but with zero
  const aprArray = []

  // Only use main farm APR if rewards are currently available
  if (rewardsAreActive(farm.rewardTokenBalance, farm.rewardStartBlock, currentBlock)) {
    totalApr = tokenRewardsApr
    const aprEntry = { symbol: 'USDM', apr: tokenRewardsApr, isCommunityControl: farm.isCommunity }
    aprArray.push(aprEntry)
  }

  // Get APR for slave farms and add to running total
  for (let i = 0; i < farm.slaveFarms.length; i++) {
    const rewardTokenPrice = new BigNumber(getTokenPriceFromAddress(farms, farm.slaveFarms[i].rewardTokenAddress))
    const { tokenRewardsApr: slaveApr } = getFarmApr(
      new BigNumber(farm.slaveFarms[i].poolWeight),
      rewardTokenPrice,
      totalLiquidity,
      new BigNumber(farm.slaveFarms[i].rewardsPerSecond)
    )

    if (rewardsAreActive(farm.slaveFarms[i].rewardTokenBalance, farm.slaveFarms[i].rewardStartBlock, currentBlock)) {
      totalApr += slaveApr
      const aprEntry = {
        symbol: farm.slaveFarms[i].rewardTokenSymbol,
        apr: slaveApr,
        isCommunityControl: farm.slaveFarms[i].isCommunityControl,
      }
      aprArray.push(aprEntry)
    }
  }

  return { aprArray, totalApr, lpRewardsApr }
}

export default null
